import Dexie, { type Table } from 'dexie';
import { v7 } from 'uuid';
import type { HttpMethod } from '@/model';
import { stableJson } from './domain';
export interface RetainedDraft {
    key: string;
    value: unknown;
    updatedAt: number;
    epoch?:string;
}
export interface Intent {
    id: string;
    key: string;
    epoch: string;
    method: HttpMethod;
    path: string;
    body: Record<string, unknown>;
    fingerprint: string;
    createdAt: number;
    headers?: Record<string, string>;
    state: 'prepared' | 'sending' | 'unknown' | 'committed' | 'review';
    completion?: {key:string;updatedAt:number;fingerprint:string;operationId?:string};
    resultTarget?: {
        kind: 'item' | 'project';
        id: string;
    };
}
async function draftFingerprint(value:unknown):Promise<string>{
    const bytes=new TextEncoder().encode(stableJson(value));
    return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256',bytes)),byte=>byte.toString(16).padStart(2,'0')).join('');
}
function safeIntent(value: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(Object.entries(value).filter(([key]) => !['access_preview_token', 'url', 'presenter_url'].includes(key)).map(([key, val]) => [key, Array.isArray(val) ? val.map(v => v && typeof v === 'object' ? safeIntent(v as Record<string, unknown>) : v) : val && typeof val === 'object' ? safeIntent(val as Record<string, unknown>) : val]));
}
export class SpacesJournal extends Dexie {
    drafts!: Table<RetainedDraft, string>;
    intents!: Table<Intent, string>;
    preferences!: Table<{
        key: string;
        value: unknown;
    }, string>;
    constructor(name: string) {
        super(name);
        this.version(1).stores({ drafts: '&key,updatedAt', intents: '&id,key,state,createdAt', preferences: '&key' });
    }
    async prepare(key: string, epoch: string, method: HttpMethod, path: string, supplied: Record<string, unknown>, headers?: Record<string, string>,reuseCommitted=true,draft?:Pick<RetainedDraft,'key'|'updatedAt'|'value'>): Promise<Intent> {
        const body = safeIntent(supplied);
        const conditions = Object.fromEntries(Object.entries(headers ?? {}).filter(([name]) => ['if-match', 'if-none-match', 'x-hylo-files-mode'].includes(name.toLowerCase())).map(([name, value]) => [name.toLowerCase(), value]));
        const fingerprint = stableJson({ method, path, body, headers: conditions });
        const operationId=draft?.value&&typeof draft.value==='object'&&'draftId'in draft.value&&typeof draft.value.draftId==='string'?draft.value.draftId:undefined;
        // Only a digest of the captured draft is retained with the operation: no proof or extra content copy.
        const completion=draft?{key:draft.key,updatedAt:draft.updatedAt,fingerprint:await draftFingerprint(draft.value),...operationId?{operationId}:{}}:undefined;
        return this.transaction('rw', this.intents, async () => {
            const previous = (await this.intents.where('key').equals(key).sortBy('createdAt')).at(-1);
            if (reuseCommitted&&previous?.state === 'committed' && previous.fingerprint === fingerprint) {
                if (previous.epoch !== epoch)
                    throw new Error('epoch_changed');
                return previous;
            }
            if(previous?.state==='committed'&&operationId&&previous.completion?.operationId===operationId)throw new Error('operation_committed');
            if (previous && previous.state !== 'committed') {
                if (previous.epoch !== epoch)
                    throw new Error('epoch_changed');
                if (previous.fingerprint === fingerprint && previous.state !== 'review')
                    return previous;
                if (previous.state === 'unknown' || previous.state === 'sending')
                    throw new Error('outcome_unknown');
            }
            const intent: Intent = { id: v7(), key, epoch, method, path, body, headers: conditions, fingerprint, createdAt: Date.now(), state: 'prepared',...completion?{completion}:{} };
            await this.intents.put(intent);
            return intent;
        });
    }
    async acknowledge(id: string, draftKey?: string, capturedUpdatedAt?: number, resultTarget?: Intent['resultTarget'], capturedDraft?: unknown): Promise<void> {
        const intent=await this.intents.get(id);
        const key=draftKey??intent?.completion?.key,at=capturedUpdatedAt??intent?.completion?.updatedAt;
        const retained=key?await this.drafts.get(key):undefined;
        const expected=capturedDraft!==undefined?await draftFingerprint(capturedDraft):intent?.completion?.fingerprint;
        const matches=retained?.updatedAt===at&&retained!==undefined&&(!expected||await draftFingerprint(retained.value)===expected);
        await this.transaction('rw', this.intents, this.drafts, async () => {
            await this.intents.update(id, { state: 'committed', ...resultTarget ? { resultTarget } : {} });
            if (matches&&key&&retained) {
                const current = await this.drafts.get(key);
                // Recheck within the write transaction: another tab may have typed while hashing.
                if (current?.updatedAt === retained.updatedAt && stableJson(current.value)===stableJson(retained.value))
                    await this.drafts.delete(key);
            }
        });
    }
}
const stores = new Map<string, SpacesJournal>();
export function journalFor(origin: string, realm: string, account: string): SpacesJournal {
    const name = `hylo-web-spaces-${encodeURIComponent(origin)}-${encodeURIComponent(realm)}-${account}`;
    let db = stores.get(name);
    if (!db) {
        db = new SpacesJournal(name);
        stores.set(name, db);
    }
    return db;
}
/** Explicit account removal is the only operation that erases retained feature work. */
export async function removeAccountJournals(origin: string, account: string): Promise<void> {
    const prefix = `hylo-web-spaces-${encodeURIComponent(origin)}-`;
    const suffix = `-${account}`;
    const names = new Set([...await Dexie.getDatabaseNames(), ...stores.keys()]);
    for (const name of names) {
        if (!name.startsWith(prefix) || !name.endsWith(suffix))
            continue;
        const db = stores.get(name);
        stores.delete(name);
        if (db)
            await db.delete();
        else
            await Dexie.delete(name);
    }
}
